import React, { ReactElement, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Navigate, useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import { APIResponse } from '@groma-dev/scaffold';
import { ToastMode, useDefineMessages, useToasts } from '@groma-dev/blueprint';
import { useIntl } from 'react-intl';
import { buildUrlWithParams, isAppRoute, isAuthenticatedRoute } from '../../utils/WebHelper';
import { useAppStore, useDataStore } from '../../providers/StoresProvider';
import { AppConfig } from '../../../core/AppConfig';
import { AppRoutes } from '../../../core/routes';
import { ComponentFactory } from '../../ComponentFactory';
import { FirebaseEvents } from '../../utils/Constants';

const AuthRouter = observer(({ children }: { children: ReactElement }) => {
  const dataStore = useDataStore();
  const { firebase } = useAppStore();
  const { pathname, search } = useLocation();
  const { formatMessage } = useIntl();
  const { addToast } = useToasts();
  const messages = useDefineMessages();
  const { userState } = dataStore;
  const { Firebase } = AppConfig.Settings.Metrics;
  const { enabled: HomeEnabled } = AppConfig.Pages.Home;
  const { name: authCookieName } = AppConfig.Settings.Authentication.cookie;

  const [currentLocation, setCurrentLocation] = useState<string>('');
  const [initialized, setInitialized] = useState<boolean>(false);

  const authCookie = Cookies.get(authCookieName);

  const firePageViewEvent = () => {
    if (Firebase.enabled) {
      if (currentLocation !== pathname) {
        setCurrentLocation(pathname);
        firebase.fireEvent(FirebaseEvents.PAGE_VIEW, { path: pathname });
      }
    }
  };

  if (!authCookie && isAuthenticatedRoute(pathname)) {
    const returnUrl = encodeURIComponent(`${pathname}${search}`);

    return <Navigate to={buildUrlWithParams(`${AppRoutes.SignIn}?return_url=${returnUrl}`)} replace />;
  } else if (authCookie) {
    if (!userState.fetchingUser && !userState.user && !initialized) {
      dataStore
        .fetchUser()
        .then(res => {
          if (res.error) {
            // allow page to render before showing toast message
            setTimeout(() => addToast(res.error?.message ?? formatMessage(messages.commonError), ToastMode.ERROR), 100);
          }
        })
        .catch((res: APIResponse) => {
          setTimeout(() => addToast(res.error?.message ?? formatMessage(messages.commonError), ToastMode.ERROR), 100);
        });
    }

    if (!isAuthenticatedRoute(pathname) && isAppRoute(pathname)) {
      return <Navigate to={buildUrlWithParams(HomeEnabled ? AppRoutes.root : AppRoutes.Profile)} replace />;
    }

    if (!HomeEnabled && pathname === AppRoutes.root) {
      return <Navigate to={buildUrlWithParams(AppRoutes.Profile)} replace />;
    }
  }

  // handle redirect for Phone Verification
  if (userState.user && !userState.user.phone_number_verified && pathname !== AppRoutes.PhoneVerification) {
    const returnUrl = encodeURIComponent(`${pathname}${search}`);
    return <Navigate to={buildUrlWithParams(`${AppRoutes.PhoneVerification}?return_url=${returnUrl}`)} />;
  }

  firePageViewEvent();

  if (!initialized) setInitialized(true);

  return isAuthenticatedRoute(pathname) && !userState.user ? <ComponentFactory.Loading isPage /> : children;
});

export default AuthRouter;
